<template>
  <v-card flat>
    <v-card-title>
      <span v-if="!selectedCustomer">Customer Invoice Comparison</span>
      <CustomerHeader v-if="selectedCustomer" :customer="selectedCustomer"/>
      <v-spacer/>
      <customer-autocomplete
        v-model="selectedCustomer"
        label="Select Customer"
        style="min-width: 400px;"
      />
      <v-spacer/>
      <v-text-field
        :loading="loading"
        :value="weekFormatted"
        append-icon="mdi-calendar"
        class="mr-2"
        label="Fed Invoice Week"
        readonly
        style="max-width: 200px"
        @click="showDatePicker=true"
      />
      <v-text-field
        :value="stripeDatesFormatted"
        append-icon="mdi-calendar"
        class="mr-2"
        label="Stripe Date Range"
        readonly
        style="max-width: 250px"
        @click="showStripeDatePicker = true"
      />

    </v-card-title>

    <v-card-text v-if="selectedCustomer && !loading">
      <!-- Stripe Date Range Info -->
      <v-alert
        class="mb-4"
        dense
        text
        type="info"
        icon="mdi-information-outline"
      >
        <div class="text-body-2">
          <strong>Stripe Date Range:</strong> This page searches for Stripe invoices and payments created between
          <strong>{{ stripeDatesFormatted }}</strong> (starting at noon PT on {{ stripeStartDayName }} and ending at
          11:59:59 PM PT on {{ stripeEndDayName }}).
          The date range must cover all Stripe activity related to the Fed invoice week
          (<strong>{{ weekFormatted }}</strong>) for accurate comparisons.
          If Stripe data is missing or comparisons seem incorrect, adjust the Stripe date range above. Note, if more
          than one week of Fed meals have been billed in this date range, this page will not work!
        </div>
      </v-alert>

      <!-- Order Too Old Warning - More than 1 week past billing date -->
      <v-alert
        v-if="isOrderTooOld"
        class="mb-4"
        prominent
        type="error"
        icon="mdi-lock"
      >
        <v-row align="center">
          <v-col>
            <div class="font-weight-bold text-h6 mb-2">
              Order Too Old for Adjustments
            </div>
            <p class="mb-0">
              This order was billed on <strong>{{ billingDateFormatted }}</strong>, which is more than one week ago.
              Adjustments can no longer be made through this page.
            </p>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Billing Date Warning - Before Noon -->
      <v-alert
        v-if="isBeforeBillingTime"
        class="mb-4"
        prominent
        type="warning"
        icon="mdi-clock-alert"
      >
        <v-row align="center">
          <v-col>
            <div class="font-weight-bold text-h6 mb-2">
              Order Not Yet Billed
            </div>
            <p class="mb-0">
              This order has not been billed yet. Billing occurs on <strong>{{ billingDateFormatted }}</strong>.
              The "Apply Adjustments" button will be disabled until after the billing time.
            </p>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Billing In Progress Warning - Between Noon and 2 PM with no Stripe invoice -->
      <v-alert
        v-if="isBillingInProgress"
        class="mb-4"
        prominent
        type="info"
        icon="mdi-clock-fast"
      >
        <v-row align="center">
          <v-col>
            <div class="font-weight-bold text-h6 mb-2">
              Billing May Be In Progress
            </div>
            <p class="mb-0">
              Billing started on <strong>{{ billingDateFormatted }}</strong> and may still be processing.
              No Stripe invoice has been found yet. Please check back shortly or proceed with caution.
            </p>
          </v-col>
        </v-row>
      </v-alert>

      <!-- Subaccount Warning -->
      <template v-if="selectedCustomer.mainAccount">
        <v-alert class="mb-4" type="warning">
          <v-row align="center">
            <v-col>
              <div class="font-weight-bold mb-2">
                <v-icon class="mr-2" color="warning">mdi-account-alert</v-icon>
                Subaccount Selected
              </div>
              <p class="mb-2">
                {{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }} is a subaccount.
                Invoice comparisons should be performed on the main account for accurate billing information.
              </p>
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="primary"
                @click="redirectToMainAccount"
              >
                <v-icon left>mdi-account-arrow-right</v-icon>
                View Main Account
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>
      </template>

      <!-- Main Content (only shown for main accounts) -->
      <template v-else>
        <v-row class="mb-4">
          <v-col>
            <v-alert
              v-if="!stripeInvoices.length && !fedInvoices.length"
              text
              type="info"
            >
              No invoices found for {{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }} in the selected
              date range.
            </v-alert>

            <v-row v-else>
              <v-col>
                <v-chip
                  class="mr-2"
                  color="success"
                  text-color="white"
                >
                  {{ realStripeInvoices.length }} Stripe Invoice{{ realStripeInvoices.length !== 1 ? 's' : '' }}
                  <span v-if="stripeCreditNotes.length > 0">, {{
                      stripeCreditNotes.length
                    }} Credit Note{{ stripeCreditNotes.length !== 1 ? 's' : '' }}</span>
                </v-chip>
                <v-chip
                  class="mr-2"
                  color="error"
                  v-if="!selectedCustomer.isValidCard"
                >
                  No Valid CCard
                </v-chip>
                <v-chip
                  class="mr-2"
                  color="error"
                  v-if="selectedCustomer.confirmed===false"
                >
                  Unconfirmed
                </v-chip>

              </v-col>

              <!--              <v-chip-->
              <!--                color="info"-->
              <!--                text-color="white"-->
              <!--              >-->
              <!--                {{ fedInvoices.length }} Fed Invoice{{ fedInvoices.length !== 1 ? 's' : '' }}-->
              <!--              </v-chip>-->
              <v-col v-if="failedPaymentInvoices.length===0 && hasAdjustments">
                <v-alert
                  color="primary"
                  icon="mdi-auto-fix"
                  outlined
                >
                  Adjustments Required in Stripe

                </v-alert>
                <v-btn @click="showComparisons=[0]">Show Adjustments</v-btn>
              </v-col>
              <v-col v-if="failedPaymentInvoices.length>0">
                <v-alert class="" outlined type="error">
                  <v-row align="center">
                    <v-col>
                      <div class="font-weight-bold mb-2">
                        Failed Payment{{ failedPaymentInvoices.length > 1 ? 's' : '' }} Detected
                      </div>
                      <div class="d-flex flex-wrap gap-2">
                        <v-chip
                          v-for="invoice in failedPaymentInvoices"
                          :key="invoice.id"
                          class="mr-2 mb-1"
                          color="error"
                          small
                          text-color="white"
                        >
                          <a
                            :href="getStripeInvoiceUrl(invoice.id)"
                            class="text-decoration-none white--text"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {{ invoice.number || invoice.id }}
                            ({{ invoice.attempt_count }} attempt{{ invoice.attempt_count > 1 ? 's' : '' }})
                            <v-icon right small>mdi-open-in-new</v-icon>
                          </a>
                        </v-chip>
                      </div>
                    </v-col>
                    <v-col>
                      <v-btn
                        :disabled="voidingAndClearing"
                        :loading="voidingInvoices"
                        class="mr-2 mb-1"
                        color="warning"
                        data-cy="void-failed-invoices"
                        outlined
                        @click="voidFailedInvoices"
                      >
                        <v-icon left small>mdi-file-cancel-outline</v-icon>
                        Void Invoice
                      </v-btn>
                      <v-btn
                        :disabled="voidingInvoices"
                        :loading="voidingAndClearing"
                        class="mb-1"
                        color="warning"
                        @click="voidInvoiceAndClearOrders"
                      >
                        <v-icon left small>mdi-credit-card-off</v-icon>
                        Void Invoice and Clear Orders From Fed
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-alert>
                <v-spacer/>
              </v-col>
              <v-col v-if="paidInvoices.length > 0 && !hasAdjustments">
                <v-alert outlined type="success">
                  <v-row align="center">
                    <v-col>
                      <div class="font-weight-bold mb-2">
                        Payment Summary
                      </div>
                      <div class="mb-2">
                        <div class="d-flex justify-space-between align-center">
                          <span>Total Amount:</span>
                          <span class="font-weight-medium">{{ formatCurrency(totalInvoiceAmount) }}</span>
                        </div>
                        <div class="d-flex justify-space-between align-center">
                          <span>Amount Paid:</span>
                          <span class="font-weight-medium text-success">{{ formatCurrency(actualAmountPaid) }}</span>
                        </div>
                        <div v-if="totalCreditUsed > 0" class="d-flex justify-space-between align-center">
                          <span>Credit Used:</span>
                          <span class="font-weight-medium text-info">{{ formatCurrency(totalCreditUsed) }}</span>
                        </div>
                        <div v-if="totalCreditNoteAmount > 0" class="d-flex justify-space-between align-center">
                          <span>Credit Issued:</span>
                          <span class="font-weight-medium text-warning">-{{
                              formatCurrency(totalCreditNoteAmount)
                            }}</span>
                        </div>
                        <div v-if="creditRemaining !== 0" class="d-flex justify-space-between align-center">
                          <span>Credit Remaining:</span>
                          <span :class="creditRemaining > 0 ? 'text-info' : 'text-error'" class="font-weight-medium">
                            {{ formatCurrency(Math.abs(creditRemaining)) }}
                            <!--                            {{ (0 < creditRemaining) ? ' owed' : '' }}-->
                          </span>
                        </div>
                        <v-divider class="my-2"></v-divider>
                        <div class="d-flex justify-space-between align-center">
                          <span class="font-weight-bold">Net Received:</span>
                          <v-chip
                            :color="netPaidAmount > 0 ? 'success' : 'warning'"
                            small
                            text-color="white"
                          >
                            <v-icon left small>mdi-cash</v-icon>
                            {{ formatCurrency(netPaidAmount) }}
                          </v-chip>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-btn
                        :disabled="netPaidAmount <= 0"
                        :loading="creditingAndCanceling"
                        color="error"
                        @click="creditAndCancelOrder"
                      >
                        <v-icon left small>mdi-cash-refund</v-icon>
                        Cancel Order and Credit
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-alert>

              </v-col>
              <v-col class="text-right">
                <v-btn
                  v-if="selectedCustomer && selectedCustomer.stripe_id && weekDate"
                  small
                  color="primary"
                  class="ml-2 mb-2"
                  outlined
                  :disabled="loading || applyingAdjustments"
                  @click="showAddCouponDialog = true"
                >
                  <v-icon left small>mdi-ticket-percent-outline</v-icon>
                  {{ couponDiscountUsed ? 'Replace Coupon' : 'Add Coupon' }}
                </v-btn>
                <v-btn
                  v-if="selectedCustomer && selectedCustomer.stripe_id"
                  small
                  :href="getStripeCustomerUrl(selectedCustomer.stripe_id)"
                  class="ml-2 mb-2"
                  outlined
                  target="_blank"
                >
                  <v-icon left small>mdi-credit-card</v-icon>
                  Customer
                  <v-icon right small>mdi-open-in-new</v-icon>
                </v-btn>
                <v-btn
                  v-if="selectedCustomer && selectedCustomer.stripe_id"
                  small
                  :to="{name:'Customer', params: {id:customerUid} }"
                  class="ml-2 mb-2"
                  outlined
                  target="_blank"
                >
                  <v-icon left small>mdi-credit-card</v-icon>
                  Fed Customer
                  <v-icon right small>mdi-open-in-new</v-icon>
                </v-btn>
                <v-btn
                  v-for="(invoice, i) of realStripeInvoices"
                  v-bind:key="i"
                  :href="getStripeInvoiceUrl(invoice.id)"
                  class="ml-2 mb-2"
                  outlined
                  target="_blank"
                  small
                >
                  <v-icon left small>mdi-credit-card</v-icon>
                  Invoice {{ i + 1 }}
                  <v-icon right small>mdi-open-in-new</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Coupon Revocation (fraudulent / TOS-violating coupon) -->
        <v-row v-if="couponDiscountUsed">
          <v-col>
            <v-alert outlined type="warning" icon="mdi-ticket-percent-outline">
              <v-row align="center">
                <v-col>
                  <div class="font-weight-bold mb-1">
                    Coupon Discount Applied
                  </div>
                  <p class="mb-0 text-body-2">
                    A coupon discount of <strong>{{ formatCurrency(couponDiscountAmount) }}</strong> was used for this
                    week. If this coupon was used fraudulently (e.g. a referral coupon that violated TOS), you can revoke
                    it. This deletes the coupon in Stripe, recalculates the Fed invoice without the discount, and prevents
                    any referral credit from being paid to the referrer.
                  </p>
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    :loading="revokingCoupon"
                    color="error"
                    @click="openRevokeCouponDialog"
                  >
                    <v-icon left small>mdi-ticket-confirmation-outline</v-icon>
                    Revoke Coupon
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>

        <!-- Failed Payment Alert -->
        <v-row v-if="failedPaymentInvoices.length > 0">
          <v-col/>
          <v-col class="align-self-center">

          </v-col>
          <v-col/>
        </v-row>

        <!-- Comparison Results -->
        <v-alert v-for="(comparison, index) in invoiceComparisons.filter(i => i.totals.total_mismatch)"
                 v-bind:key="`alert-${index}`" type="warning">
          The totals are wrong!
          Amount in Fed {{ formatCurrency(comparison.totals.total_mismatch.fed) }} vs amount in Stripe
          {{ formatCurrency(comparison.totals.total_mismatch.stripe) }}. Difference:
          {{ formatCurrency(comparison.totals.total_mismatch.difference) }}
        </v-alert>
        <v-row v-if="invoiceComparisons.length">
          <v-col cols="12">
            <h3 class="mb-3">Invoice Comparisons</h3>
            <v-expansion-panels v-model="showComparisons" multiple>
              <v-expansion-panel
                v-for="(comparison, index) in invoiceComparisons"
                :key="`comparison-${index}`"
                :value="`comparison-${index}`"
              >
                <v-expansion-panel-header>
                  <div>
                    <v-icon
                      :color="hasAdjustments ? 'warning' : 'success'"
                      class="mr-2"
                    >
                      {{ hasAdjustments ? 'mdi-alert' : 'mdi-check' }}
                    </v-icon>
                    <span>
                    Stripe {{ comparison.stripeInvoice.number || comparison.stripeInvoice.id }}
                    vs Fed Invoice
                  </span>
                    <v-chip
                      v-if="hasAdjustments"
                      class="ml-2"
                      color="warning"
                      small
                    >
                      {{ countAdjustments }} differences
                    </v-chip>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content>
                  <div v-if="!hasAdjustments" class="text-success">
                    ✓ Invoices match perfectly. Note that free items may still appear on stripe invoices as there is no
                    amount to credited.
                  </div>

                  <div v-else>
                    <!-- Total Differences -->
                    <v-toolbar flat>
                      <v-spacer/>
                      <v-btn
                        :disabled="!hasAdjustments || isBeforeBillingTime || isOrderTooOld"
                        :loading="applyingAdjustments"
                        color="primary"
                        @click="applyAdjustments"
                      >
                        <v-icon left>mdi-auto-fix</v-icon>
                        Apply Adjustments in Stripe
                      </v-btn>
                      <v-tooltip v-if="isBeforeBillingTime || isOrderTooOld" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon class="ml-2" :color="isOrderTooOld ? 'error' : 'warning'" v-bind="attrs" v-on="on">
                            mdi-information
                          </v-icon>
                        </template>
                        <span v-if="isOrderTooOld">This order is too old for adjustments (more than 1 week past billing date)</span>
                        <span v-else-if="isBeforeBillingTime">Order has not been billed yet. Billing occurs on {{
                            billingDateFormatted
                          }}</span>
                      </v-tooltip>
                    </v-toolbar>
                  </div>

                  <!-- Adjustments Panel -->
                  <v-row v-if="hasAdjustments">
                    <v-col cols="12">
                      <div>
                        <div>
                          <div>
                            <div class="adjustments-content">
                              <div class="d-flex justify-space-between align-center mb-4">
                                <p class="text--secondary ma-0">
                                  The following line item adjustments are required to make the Stripe invoice match the
                                  Fed invoice:
                                </p>
                              </div>

                              <!-- Items to Remove from Stripe -->
                              <div v-if="adjustments.itemsToRemove.length > 0" class="mb-4">
                                <h4 class="red--text">Remove from Stripe ({{ adjustments.itemsToRemove.length }}
                                  items)</h4>
                                <v-simple-table class="mt-2" dense>
                                  <template v-slot:default>
                                    <thead>
                                    <tr>
                                      <th>Action</th>
                                      <th>Description</th>
                                      <th>Quantity</th>
                                      <th>Amount</th>
                                      <th>Total Amount</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(item, idx) in adjustments.itemsToRemove" :key="`remove-${idx}`">
                                      <td>
                                        <v-chip color="red" small text-color="white">
                                          <v-icon left small>mdi-minus</v-icon>
                                          Remove
                                        </v-chip>
                                      </td>
                                      <td>{{ item.description }}</td>
                                      <td>{{ item.quantity }}</td>
                                      <td>{{
                                          formatCurrency(item.amount || item.unit_amount_excluding_tax * item.quantity)
                                        }}
                                      </td>
                                      <td>{{ formatCurrency(item.unit_amount_excluding_tax * item.quantity) }}</td>
                                    </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </div>

                              <!-- Items to Add to Stripe -->
                              <div v-if="comparison.items.missing_in_stripe.length > 0" class="mb-4">
                                <h4 class="green--text">Add to Stripe ({{ comparison.items.missing_in_stripe.length }}
                                  items)</h4>
                                <v-simple-table class="mt-2" dense>
                                  <template v-slot:default>
                                    <thead>
                                    <tr>
                                      <th>Action</th>
                                      <th>Description</th>
                                      <th>Quantity</th>
                                      <th>Unit Amount</th>
                                      <th>Total Amount</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(item, idx) in comparison.items.missing_in_stripe" :key="`add-${idx}`">
                                      <td>
                                        <v-chip color="green" small text-color="white">
                                          <v-icon left small>mdi-plus</v-icon>
                                          Add
                                        </v-chip>
                                      </td>
                                      <td>{{ item.description }}</td>
                                      <td>{{ item.quantity }}</td>
                                      <td>{{ formatCurrency(item.unit_amount) }}</td>
                                      <td>{{ formatCurrency(item.unit_amount * item.quantity) }}</td>
                                    </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </div>

                              <!-- Quantity Adjustments -->
                              <div v-if="comparison.items.quantity_differences.length > 0" class="mb-4">
                                <h4 class="orange--text">Update Quantities
                                  ({{ comparison.items.quantity_differences.length }} items)</h4>
                                <v-simple-table class="mt-2" dense>
                                  <template v-slot:default>
                                    <thead>
                                    <tr>
                                      <th>Action</th>
                                      <th>Description</th>
                                      <th>Current Qty</th>
                                      <th>Required Qty</th>
                                      <th>Change</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(item, idx) in comparison.items.quantity_differences"
                                        :key="`qty-${idx}`">
                                      <td>
                                        <v-chip color="orange" small text-color="white">
                                          <v-icon left small>mdi-pencil</v-icon>
                                          Update
                                        </v-chip>
                                      </td>
                                      <td>{{ item.description }}</td>
                                      <td>{{ item.stripe_quantity }}</td>
                                      <td>{{ item.fed_quantity }}</td>
                                      <td class="font-weight-bold">
                              <span :class="item.difference > 0 ? 'green--text' : 'red--text'">
                                {{ item.difference > 0 ? '+' : '' }}{{ item.difference }}
                              </span>
                                      </td>
                                    </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </div>

                              <!-- Amount Adjustments -->
                              <div v-if="comparison.items.amount_differences.length > 0" class="mb-4">
                                <h4 class="purple--text">Update Amounts ({{
                                    comparison.items.amount_differences.length
                                  }} items)</h4>
                                <v-simple-table class="mt-2" dense>
                                  <template v-slot:default>
                                    <thead>
                                    <tr>
                                      <th>Action</th>
                                      <th>Description</th>
                                      <th>Current Amount</th>
                                      <th>Required Amount</th>
                                      <th>Difference</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(item, idx) in comparison.items.amount_differences" :key="`amt-${idx}`">
                                      <td>
                                        <v-chip color="purple" small text-color="white">
                                          <v-icon left small>mdi-currency-usd</v-icon>
                                          Update
                                        </v-chip>
                                      </td>
                                      <td>{{ item.description }}</td>
                                      <td>{{ formatCurrency(item.stripe_total) }}</td>
                                      <td>{{ formatCurrency(item.fed_total) }}</td>
                                      <td class="font-weight-bold">
                              <span :class="item.difference > 0 ? 'green--text' : 'red--text'">
                                {{ formatCurrency(item.difference) }}
                              </span>
                                      </td>
                                    </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </div>

                              <!-- Summary of Total Impact -->
                              <div v-if="adjustmentSummary" class="mt-4 pa-3"
                                   style="background-color: #f5f5f5; border-radius: 4px;">
                                <h4 class="mb-2">Net Impact on Stripe Invoice</h4>
                                <div class="d-flex justify-space-between">
                                  <span>Items to Credit:</span>
                                  <span class="red--text">-{{ formatCurrency(adjustmentSummary.removeAmount) }}</span>
                                </div>
                                <div class="d-flex justify-space-between">
                                  <span>Items to Charge:</span>
                                  <span class="green--text">+{{ formatCurrency(adjustmentSummary.addAmount) }}</span>
                                </div>
                                <v-divider class="my-2"></v-divider>
                                <div class="d-flex justify-space-between font-weight-bold">
                                  <span>Total Net Change:</span>
                                  <span :class="adjustmentSummary.netChange >= 0 ? 'green--text' : 'red--text'">
                        {{ adjustmentSummary.netChange >= 0 ? '+' : '' }}{{
                                      formatCurrency(adjustmentSummary.netChange)
                                    }}
                      </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-col>
        </v-row>


        <!-- Individual Invoice Lists -->
        <v-row>
          <!-- Stripe Invoices -->
          <v-col cols="6">
            <div class="d-flex justify-space-between align-center mb-3">
              <h3>Stripe Invoices</h3>
              <v-btn
                outlined
                small
                @click="showIndividualInvoices = !showIndividualInvoices"
              >
                <v-icon left small>{{ showIndividualInvoices ? 'mdi-view-grid' : 'mdi-view-list' }}</v-icon>
                {{ showIndividualInvoices ? 'Show Combined' : 'Show Individual' }}
              </v-btn>
            </div>

            <!-- Combined Invoice View (default) -->
            <v-card
              v-if="!showIndividualInvoices && combinedStripeInvoice"
              class="mb-2"
              outlined
            >
              <v-card-subtitle>
                <div class="d-flex justify-space-between">
                  <div class="d-flex flex-wrap align-center">
                    <template v-if="combinedStripeInvoice._isFakeEmptyInvoice">
                      <span class="text--disabled mr-2">No Stripe Invoice Found</span>
                    </template>
                    <template v-else>
                      Combined Totals
                    </template>
                  </div>
                  <v-chip
                    v-if="combinedStripeInvoice._isFakeEmptyInvoice"
                    color="grey"
                    small
                    text-color="white"
                  >
                    Missing
                  </v-chip>
                </div>
              </v-card-subtitle>
              <v-card-text>
                <div><strong>Subtotal:</strong> {{ formatCurrency(combinedStripeInvoice.subtotal) }}</div>
                <div v-if="combinedStripeInvoice.tax"><strong>Tax:</strong> {{
                    formatCurrency(combinedStripeInvoice.tax)
                  }}
                </div>
                <div v-if="combinedStripeInvoice.amount_refunded">
                  <strong>Refunded:</strong> -{{ formatCurrency(combinedStripeInvoice.amount_refunded) }}
                  <span class="text--secondary">(refund, not a credit note)</span>
                </div>
                <div class="font-weight-bold"><strong>Total Due:</strong> {{
                    formatCurrency(combinedStripeInvoice.total)
                  }}
                </div>
                <div><strong>Items:</strong> {{
                    combinedStripeInvoice.lines && combinedStripeInvoice.lines.data ? combinedStripeInvoice.lines.data.length : 0
                  }}
                </div>
                <div><strong>Credit Notes:</strong> {{ stripeCreditNotes.length }}</div>
                <v-alert
                  v-if="refundedStripeInvoices.length"
                  class="mt-3 mb-0"
                  dense
                  text
                  type="info"
                >
                  Refunded invoice{{ refundedStripeInvoices.length === 1 ? '' : 's' }}
                  {{ refundedStripeInvoices.map(i => i.number || i.id).join(', ') }}
                  ignored: the payment was refunded in full (not through a credit note), so the customer did not pay for those lines.
                </v-alert>
                <v-alert
                  v-if="staleStripeInvoices.length"
                  class="mt-3 mb-0"
                  dense
                  text
                  type="info"
                >
                  Older $0 coupon invoice{{ staleStripeInvoices.length === 1 ? '' : 's' }}
                  {{ staleStripeInvoices.map(i => i.number || i.id).join(', ') }}
                  ignored: superseded by {{ combinedStripeInvoice._originalInvoices[0].number }}.
                  A $0 invoice cannot be credited, so its lines are not adjustments.
                </v-alert>

                <v-divider class="my-4"/>

                <div v-for="(invoice) in combinedStripeInvoice._originalInvoices"
                     :key="`invoice-total-${invoice.id}`">
                  <h6 class="mb-2">
                    Invoice
                    ({{ formatDate(invoice.created) }})
                    <a
                      :href="getStripeInvoiceUrl(invoice.id)"
                      class="text-decoration-none"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {{ invoice.number || invoice.id }}
                      <v-chip :color="getStatusColor(invoice.status)" x-small>
                        {{ invoice.status }}
                      </v-chip>
                      <v-chip v-if="invoice.amount_refunded" class="ml-1" color="orange" text-color="white" x-small>
                        refunded {{ formatCurrency(invoice.amount_refunded) }}
                      </v-chip>
                      <v-icon class="ml-1" color="primary" small>mdi-open-in-new</v-icon>
                    </a>
                  </h6>
                </div>


                <div v-for="(creditNote) in stripeCreditNotes"
                     :key="`credit-note-total-${creditNote.id}`">
                  <h6 class="mb-2">
                    Credit Note
                    ({{ formatDate(creditNote.created) }})
                    <a
                      :href="getStripeCreditNoteUrl(creditNote.id)"
                      class="text-decoration-none"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {{ creditNote.number || creditNote.id }}
                      <v-icon class="ml-1" color="primary" small>mdi-open-in-new</v-icon>
                    </a>
                  </h6>
                </div>

              </v-card-text>
            </v-card>

            <!-- Individual Invoices View -->
            <template v-if="showIndividualInvoices">
              <v-card
                v-for="invoice in stripeInvoices"
                :key="`stripe-${invoice.id}`"
                class="mb-2"
                outlined
              >
                <v-card-subtitle>
                  <div class="d-flex justify-space-between">
                    <template v-if="invoice._isFakeEmptyInvoice">
                    <span class="text--disabled">
                      No Stripe Invoice Found
                    </span>
                    </template>
                    <template v-else>
                      <a
                        :href="getStripeInvoiceUrl(invoice.id)"
                        class="text-decoration-none"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {{ invoice.number || invoice.id }}
                        <v-icon class="ml-1" color="primary" small>mdi-open-in-new</v-icon>
                      </a>
                    </template>
                    <v-chip
                      :color="invoice._isFakeEmptyInvoice ? 'grey' : getStatusColor(invoice.status)"
                      small
                      text-color="white"
                    >
                      {{ invoice._isFakeEmptyInvoice ? 'Missing' : invoice.status }}
                    </v-chip>
                    <v-chip v-if="invoice.amount_refunded" class="ml-1" color="orange" small text-color="white">
                      refunded {{ formatCurrency(invoice.amount_refunded) }}
                    </v-chip>
                  </div>
                </v-card-subtitle>
                <v-card-text>
                  <div><strong>Subtotal:</strong> {{ formatCurrency(invoice.subtotal) }}</div>
                  <div v-if="invoice.tax"><strong>Tax:</strong> {{ formatCurrency(invoice.tax) }}</div>
                  <div class="font-weight-bold"><strong>Total:</strong> {{ formatCurrency(invoice.total) }}</div>
                  <div><strong>Items:</strong> {{ invoice.lines && invoice.lines.data ? invoice.lines.data.length : 0 }}
                  </div>
                  <div><strong>Created:</strong> {{ formatDate(invoice.created) }}</div>
                </v-card-text>
              </v-card>
            </template>

            <!-- Credit Notes -->
            <template v-if="stripeCreditNotes.length > 0 && showIndividualInvoices">
              <h4 class="mt-4 mb-2">Credit Notes</h4>
              <v-card
                v-for="creditNote in stripeCreditNotes"
                :key="`credit-note-${creditNote.id}`"
                class="mb-2"
                outlined
              >
                <v-card-subtitle>
                  <div class="d-flex justify-space-between">
                    <a
                      :href="getStripeCreditNoteUrl(creditNote.id)"
                      class="text-decoration-none"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {{ creditNote.number || creditNote.id }}
                      {{ creditNote.created_at }}
                      <v-icon class="ml-1" color="primary" small>mdi-open-in-new</v-icon>
                    </a>
                    <v-chip
                      color="orange"
                      small
                      text-color="white"
                    >
                      Credit Note
                    </v-chip>
                  </div>
                </v-card-subtitle>
                <v-card-text>
                  <div><strong>Amount:</strong> {{ formatCurrency(creditNote.amount) }}</div>
                  <div><strong>Subtotal:</strong> {{ formatCurrency(creditNote.subtotal) }}</div>
                  <div><strong>Tax:</strong> {{
                      formatCurrency(creditNote.tax_amounts ? creditNote.tax_amounts.reduce((sum, tax) => sum + tax.amount, 0) : 0)
                    }}
                  </div>
                  <div><strong>Items:</strong>
                    {{ creditNote.lines && creditNote.lines.data ? creditNote.lines.data.length : 0 }}
                  </div>
                  <div><strong>Created:</strong> {{ formatDate(creditNote.created) }}</div>
                  <div v-if="creditNote.reason"><strong>Reason:</strong> {{ creditNote.reason.replace('_', ' ') }}</div>
                  <div v-if="creditNote.invoice"><strong>Original Invoice:</strong>
                    <a
                      :href="getStripeInvoiceUrl(creditNote.invoice)"
                      class="text-decoration-none"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {{ creditNote.invoice }}
                      <v-icon class="ml-1" color="primary" small>mdi-open-in-new</v-icon>
                    </a>
                  </div>
                </v-card-text>
              </v-card>
            </template>

            <v-alert v-if="!stripeInvoices.length && !stripeCreditNotes.length" text type="info">
              No Stripe invoices or credit notes found
            </v-alert>

          </v-col>

          <!-- Fed Invoices -->
          <v-col cols="6">
            <h3 class="mb-3">Fed Invoices</h3>
            <v-card
              v-for="(invoice, index) in fedInvoices"
              :key="`fed-${index}`"
              class="mb-2"
              outlined
            >
              <v-card-subtitle>
                <div class="d-flex justify-space-between">
                  <span>Fed Invoice {{ index + 1 }}</span>
                  <span>
                  <v-btn
                    :to="getOrderEditRoute()"
                    class="mr-2"
                    outlined
                    small
                  >
                    <v-icon left small>mdi-pencil</v-icon>
                    Edit Order
                  </v-btn>
                  <v-btn
                    :loading="clearingOrder"
                    color="error"
                    outlined
                    small
                    @click="clearOrder"
                  >
                    <v-icon left small>mdi-delete</v-icon>
                    Clear Order
                  </v-btn>
                  </span>

                </div>
              </v-card-subtitle>
              <v-card-text>
                <div><strong>Subtotal:</strong> {{ formatCurrency(invoice.total) }}</div>
                <div v-if="invoice.totalDiscount"><strong>Discount:</strong> -{{
                    formatCurrency(invoice.totalDiscount)
                  }}
                </div>
                <div v-if="invoice.totalTaxes"><strong>Tax:</strong> {{ formatCurrency(invoice.totalTaxes) }}</div>
                <div v-if="invoice.credit"><strong>Credit:</strong> -{{ formatCurrency(invoice.credit) }}</div>
                <div class="font-weight-bold"><strong>Total Due:</strong> {{ formatCurrency(invoice.totalDue) }}</div>
                <div><strong>Items:</strong> {{ invoice.invoiceItems ? invoice.invoiceItems.length : 0 }}</div>
              </v-card-text>


              <v-expand-transition>
                <div v-show="expandedFedInvoices[index]">
                  <customer-invoice :invoice="invoice"/>
                </div>
              </v-expand-transition>
            </v-card>

            <v-alert v-if="!fedInvoices.length" text type="info">
              No Fed invoices found
            </v-alert>
          </v-col>
        </v-row>

        <!-- Line Items Row -->
        <v-row>
          <!-- Stripe Line Items -->
          <v-col cols="6">
            <v-expand-transition>
              <div v-show="showStripeLineItems && stripeInvoices.length">
                <v-card class="mt-3" outlined>
                  <v-card-subtitle>
                    {{ showIndividualInvoices ? 'Individual Stripe Line Items' : 'Combined Stripe Line Items' }}
                  </v-card-subtitle>
                  <v-card-text>
                    <div v-for="(invoice, invoiceIndex) in sortedStripeInvoices" :key="`stripe-lines-${invoice.id}`">
                      <h5 v-if="showIndividualInvoices && stripeInvoices.length > 1" class="mb-2">
                        Invoice
                        <a
                          :href="getStripeInvoiceUrl(invoice.id)"
                          class="text-decoration-none"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {{ invoice.number || invoice.id }}
                          <v-icon class="ml-1" color="primary" small>mdi-open-in-new</v-icon>
                        </a>
                      </h5>

                      <!-- Regular Items Section -->
                      <div v-if="invoice.regularLines && invoice.regularLines.length">
                        <h6 class="mb-2">Items</h6>
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                            <tr>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Amount</th>
                              <th>Tax</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(line, lineIndex) in invoice.regularLines" :key="`stripe-regular-${lineIndex}`">
                              <td class="text-caption">{{ line.description }}</td>
                              <td>{{ line.quantity }}</td>
                              <td>{{ formatCurrency(line.amount) }}</td>
                              <td>{{ formatCurrency(getStripeLineTax(line)) }}</td>
                            </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>

                      <!-- Shipping Items Section -->
                      <div v-if="invoice.shippingLines && invoice.shippingLines.length" class="mt-4">
                        <h6 class="mb-2">Shipping</h6>
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                            <tr>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Amount</th>
                              <th>Tax</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(line, lineIndex) in invoice.shippingLines"
                                :key="`stripe-shipping-${lineIndex}`">
                              <td class="text-caption">{{ line.description }}</td>
                              <td>{{ line.quantity }}</td>
                              <td>{{ formatCurrency(line.amount) }}</td>
                              <td>{{ formatCurrency(getStripeLineTax(line)) }}</td>
                            </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>

                      <v-divider v-if="invoiceIndex < sortedStripeInvoices.length - 1" class="my-2"></v-divider>
                    </div>

                    <!-- Credit Notes Line Items -->
                    <template v-if="stripeCreditNotes.length > 0">
                      <v-divider class="my-4"></v-divider>
                      <h5 class="mb-2">Credit Note Line Items</h5>
                      <div v-for="(creditNote, creditNoteIndex) in stripeCreditNotes"
                           :key="`credit-note-lines-${creditNote.id}`">
                        <h6 class="mb-2">
                          Credit Note
                          ({{ formatDate(creditNote.created) }})
                          <a
                            :href="getStripeCreditNoteUrl(creditNote.id)"
                            class="text-decoration-none"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {{ creditNote.number || creditNote.id }}
                            <v-icon class="ml-1" color="primary" small>mdi-open-in-new</v-icon>
                          </a>
                        </h6>

                        <!-- Credit Note Items -->
                        <div v-if="creditNote.lines && creditNote.lines.data && creditNote.lines.data.length">
                          <v-simple-table dense>
                            <template v-slot:default>
                              <thead>
                              <tr>
                                <th>Description</th>
                                <th>Qty</th>
                                <th>Amount</th>
                                <th>Tax</th>
                              </tr>
                              </thead>
                              <tbody>
                              <tr v-for="(line, lineIndex) in creditNote.lines.data"
                                  :key="`credit-note-line-${lineIndex}`">
                                <td class="text-caption">{{ line.description }}</td>
                                <td>{{ line.quantity }}</td>
                                <td>{{ formatCurrency(-Math.abs(line.amount)) }}</td>
                                <td>{{ formatCurrency(-Math.abs(getStripeLineTax(line))) }}</td>
                              </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                        </div>

                        <v-divider v-if="creditNoteIndex < stripeCreditNotes.length - 1" class="my-2"></v-divider>
                      </div>
                    </template>
                  </v-card-text>
                </v-card>
              </div>
            </v-expand-transition>
          </v-col>

          <!-- Fed Line Items -->
          <v-col cols="6">
            <v-expand-transition>
              <div v-show="showFedLineItems && fedInvoices.length">
                <v-card class="mt-3" outlined>
                  <v-card-subtitle class="d-flex align-center">
                    Fed Line Items
                    <v-spacer/>
                    <v-checkbox
                      v-model="showFedPriceDetails"
                      label="Show price details"
                      hide-details
                      dense
                      class="mt-0 pt-0"
                    />
                  </v-card-subtitle>
                  <v-card-text>
                    <!-- Cheaper-plan check across plans available on the website -->
                    <template v-if="planCostCheck">
                      <v-alert
                        v-if="planCostCheck.cheaper.length"
                        dense text type="success" class="mb-3"
                      >
                        <div class="font-weight-medium">
                          Cheaper on {{ planCostCheck.cheaper.length }}
                          {{ planCostCheck.cheaper.length === 1 ? 'plan' : 'plans' }}
                          (current plan {{ planCostCheck.currentPlanLabel }}:
                          {{ formatCurrency(planCostCheck.currentTotal) }})
                        </div>
                        <ul>
                          <li
                            v-for="opt in planCostCheck.cheaper"
                            :key="`cheaper-${opt.planId}`"
                            class="text-caption"
                          >
                            {{ opt.planName }}: {{ formatCurrency(opt.total) }}
                            (saves {{ formatCurrency(-opt.difference) }})
                          </li>
                        </ul>
                        <b>Note: this is experimental, try it out before recommending to a customer!</b>
                      </v-alert>
                      <v-alert v-else dense text type="info" class="mb-3">
                        Already on the cheapest available plan
                        (current plan {{ planCostCheck.currentPlanLabel }}:
                        {{ formatCurrency(planCostCheck.currentTotal) }}).
                      </v-alert>
                    </template>

                    <div v-for="(invoice, invoiceIndex) in sortedFedInvoices" :key="`fed-lines-${invoiceIndex}`">
                      <h5 v-if="fedInvoices.length > 1" class="mb-2">Fed Invoice {{ invoiceIndex + 1 }}</h5>

                      <!-- Regular Items Section -->
                      <div v-if="invoice.regularItems && invoice.regularItems.length">
                        <h6 class="mb-2">Items</h6>
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                            <tr>
                              <th>Ship Date</th>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Amount</th>
                              <th>Tax</th>
                            </tr>
                            </thead>
                            <tbody>
                            <template v-for="(item, itemIndex) in invoice.regularItems">
                              <tr
                                :key="`fed-regular-${itemIndex}`"
                                style="cursor: pointer"
                                @click="toggleFedRow(`${invoiceIndex}-${itemIndex}`)"
                              >
                                <td>{{ item.ship_date || '-' }}</td>
                                <td class="text-caption">
                                  {{ item.description }}
                                  <v-chip v-if="item.is_substitution" x-small label color="purple" outlined
                                          class="ml-1">
                                    {{ item.is_allergy_substitution ? 'Allergy sub' : 'Sub' }}
                                  </v-chip>
                                  <v-chip v-if="item.order_type === 'chefs_choice'" x-small label color="teal" outlined
                                          class="ml-1">
                                    Chef's choice
                                  </v-chip>
                                  <v-chip v-if="item.meal_size === 'large'" x-small label color="orange" outlined
                                          class="ml-1">
                                    Large
                                  </v-chip>
                                </td>
                                <td>{{ item.quantity }}</td>
                                <td>{{ formatCurrency(item.total) }}</td>
                                <td>{{ formatCurrency(getFedItemTax(item)) }}</td>
                              </tr>
                              <tr
                                v-if="isFedRowExpanded(`${invoiceIndex}-${itemIndex}`)"
                                :key="`fed-regular-detail-${itemIndex}`"
                                class="price-detail-row"
                              >
                                <td></td>
                                <td colspan="4" class="text-caption grey--text pt-0 pb-2">
                                  <v-icon x-small class="mr-1">mdi-calculator-variant-outline</v-icon>
                                  {{ explainItemPrice(item) }}
                                  <div v-if="explainItemWarnings(item)" class="red--text font-weight-medium mt-1">
                                    ⚠ {{ explainItemWarnings(item) }}
                                  </div>
                                </td>
                              </tr>
                            </template>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>

                      <!-- Shipping Items Section -->
                      <div v-if="invoice.shippingItems && invoice.shippingItems.length" class="mt-4">
                        <h6 class="mb-2">Shipping</h6>
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                            <tr>
                              <th>Ship Date</th>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Amount</th>
                              <th>Tax</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(item, itemIndex) in invoice.shippingItems" :key="`fed-shipping-${itemIndex}`">
                              <td>{{ item.ship_date || '-' }}</td>
                              <td class="text-caption">{{ item.description }}</td>
                              <td>{{ item.quantity }}</td>
                              <td>{{ formatCurrency(item.total) }}</td>
                              <td>{{ formatCurrency(getFedItemTax(item)) }}</td>
                            </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>

                      <v-divider v-if="invoiceIndex < fedInvoices.length - 1" class="my-2"></v-divider>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-expand-transition>

            <!-- Active plan prices, beneath the Fed line items -->
            <PlanPrices :customer="selectedCustomer"/>
          </v-col>
        </v-row>
      </template>
    </v-card-text>

    <v-card-text v-if="!selectedCustomer && !loading">
      <v-alert text type="info">
        Please select a customer to view their invoices.
      </v-alert>
    </v-card-text>

    <v-dialog
      v-model="showDatePicker"
      persistent
      width="290px"
    >
      <v-date-picker
        v-model="weekDate"
        :allowed-dates="isSunday"
        no-title
      ></v-date-picker>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="closeDatePicker">Close</v-btn>
      </v-card-actions>
    </v-dialog>

    <v-dialog
      v-model="showStripeDatePicker"
      persistent
      width="290px"
    >
      <v-date-picker
        v-model="stripeDates"
        no-title
        range
      ></v-date-picker>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="showStripeDatePicker = false">Close</v-btn>
      </v-card-actions>
    </v-dialog>

    <!-- Void Invoices Dialog -->
    <v-dialog
      v-model="showVoidDialog"
      persistent
      width="500px"
    >
      <v-card>
        <v-card-title>
          <v-icon class="mr-2" color="warning">mdi-alert</v-icon>
          Void Unpaid Invoices?
        </v-card-title>
        <v-card-text>
          <p>The following invoices are not paid and will prevent adjustments:</p>
          <v-list dense>
            <v-list-item
              v-for="invoice in nonPaidInvoices"
              :key="invoice.id"
              class="pl-0"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ invoice.number || invoice.id }}
                  <v-chip :color="getStatusColor(invoice.status)" class="ml-2" x-small>
                    {{ invoice.status }}
                  </v-chip>
                  <v-chip v-if="invoice.amount_refunded" class="ml-1" color="orange" text-color="white" x-small>
                    refunded {{ formatCurrency(invoice.amount_refunded) }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <p class="mt-3">Would you like to void these invoices? You can then check the adjustments and try again.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            :disabled="voidingInvoices"
            text
            @click="cancelVoidDialog"
          >
            Cancel
          </v-btn>
          <v-spacer/>
          <v-btn
            :loading="voidingInvoices"
            color="warning"
            @click="voidInvoicesAndProceed"
          >
            Void Invoices
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Clear Order Confirmation Dialog -->
    <v-dialog
      v-model="showClearOrderDialog"
      persistent
      width="600px"
    >
      <v-card v-if="selectedCustomer">
        <v-card-title>
          <v-icon class="mr-2" color="error">mdi-delete</v-icon>
          Clear All Orders?
        </v-card-title>
        <v-card-text>
          <v-alert class="mb-4" dense outlined type="warning">
            <div class="font-weight-bold">This will permanently delete all orders for:</div>
            <div class="mt-2">
              <strong>{{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }} - {{ weekFormatted }}</strong>
            </div>
            <div v-if="selectedCustomer.subAccounts && Object.keys(selectedCustomer.subAccounts).length > 0">
              <strong>Including {{ Object.keys(selectedCustomer.subAccounts).length }} subaccount(s)</strong>
            </div>
          </v-alert>

          <div v-if="orderItemsToDelete.length > 0">
            <h4 class="mb-2">Items to be cleared ({{ orderItemsToDelete.length }}):</h4>
            <v-list class="overflow-y-auto" dense max-height="300px">
              <v-list-item
                v-for="(item, index) in orderItemsToDelete"
                :key="index"
                class="px-0"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    {{ item.description }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span v-if="item.ship_date">{{ item.ship_date }} - </span>
                    Qty: {{ item.quantity }} × {{ formatCurrency(item.unit_price || item.total / item.quantity) }}
                    = {{ formatCurrency(item.total) }}
                    <span v-if="item.first_name" class="ml-2 font-italic">({{
                        item.first_name
                      }} {{ item.last_name }})</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <div class="mt-3 pa-2" style="background-color: #f5f5f5; border-radius: 4px;">
              <div class="font-weight-bold">Total Value: {{ formatCurrency(totalOrderValue) }}</div>
            </div>
          </div>

          <div v-else class="text-center py-4">
            <v-icon color="info" large>mdi-information</v-icon>
            <p class="mt-2 mb-0">No orders found for this week.</p>
          </div>

          <p class="mt-4 mb-0 font-weight-bold text--primary">
            This action cannot be undone. Are you sure you want to proceed?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            :disabled="clearingOrder"
            text
            @click="cancelClearOrder"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="orderItemsToDelete.length === 0"
            :loading="clearingOrder"
            color="error"
            @click="confirmClearOrder"
          >
            Clear {{ orderItemsToDelete.length }} Item{{ orderItemsToDelete.length !== 1 ? 's' : '' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Revoke Coupon Confirmation Dialog -->
    <v-dialog
      v-model="showRevokeCouponDialog"
      persistent
      width="560px"
    >
      <v-card v-if="selectedCustomer">
        <v-card-title>
          <v-icon class="mr-2" color="error">mdi-ticket-confirmation-outline</v-icon>
          Revoke Coupon?
        </v-card-title>
        <v-card-text>
          <v-alert class="mb-4" dense outlined type="warning">
            <div class="font-weight-bold">This will revoke the coupon used by:</div>
            <div class="mt-2">
              <strong>{{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }} - {{ weekFormatted }}</strong>
            </div>
          </v-alert>

          <p class="mb-2">The following will happen:</p>
          <ul class="mb-4">
            <li>The coupon is <strong>deleted in Stripe</strong> (the durable "revoked" marker).</li>
            <li>The Fed invoice is recalculated <strong>without the {{ formatCurrency(couponDiscountAmount) }}
              discount</strong>. The next adjustments run rebills the difference in Stripe.</li>
            <li>Any pending <strong>referral credit is prevented</strong> — the referral job can no longer retrieve the
              coupon, so the referrer is not paid out.</li>
          </ul>

          <p class="mb-0 font-weight-bold text--primary">
            Use this only for coupons used fraudulently (e.g. a referral coupon that violated TOS).
            This action cannot be undone. Are you sure?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            :disabled="revokingCoupon"
            text
            @click="cancelRevokeCoupon"
          >
            Cancel
          </v-btn>
          <v-btn
            :loading="revokingCoupon"
            color="error"
            @click="confirmRevokeCoupon"
          >
            Revoke Coupon
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add a coupon to this week's invoice only: the week is reissued with it -->
    <coupon-apply-dialog
      v-if="selectedCustomer"
      v-model="showAddCouponDialog"
      :uid="customerUid"
      :customer-name="`${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim()"
      :week-date="weekDate"
      @applied="onCouponApplied"
    />

  </v-card>
</template>

<script>
import api from "@/api";
import moment from "moment-timezone";
import {formatCurrency} from "@/store/utils";
import CustomerAutocomplete from "@/components/CustomerAutocomplete";
import CustomerInvoice from "@/components/CustomerInvoice";
import CustomerHeader from "@/components/CustomerHeader";
import CouponApplyDialog from "@/components/CouponApplyDialog";
import PlanPrices from "@/components/PlanPrices";
import urlState from "../router/urlState";
import {explainLineItemPrice, comparePlanCost} from "fed-lib/src/billing/prices-explain";
import {dropStaleCouponZeroInvoices} from "fed-lib/src/billing/stripe-invoice-filters";

// A refund made outside a credit note (the Stripe dashboard, the account-move
// clean-up) is recorded on the week's payment record (fed-lib
// invoice-payments) and v2/stripe/invoices joins it onto each invoice as
// amount_refunded. A credit note already reduces the invoice; a plain refund
// does not, so the page has to take it off itself.
const refundedAmount = invoice => (invoice && invoice.amount_refunded) || 0;

const OLD_STRIPE_ACCOUNT = 'acct_1B9PlYAhKDCZ2p2o';
const NEW_STRIPE_ACCOUNT = 'acct_1GjcI1D0KVemEdeG';
const isFullyRefunded = invoice => !!invoice
  && !invoice._isFakeEmptyInvoice
  && (invoice.amount_paid || 0) > 0
  && refundedAmount(invoice) >= invoice.amount_paid;

const createFakeEmptyInvoice = () => ({
  id: 'no-stripe-invoice',
  number: 'NO-INVOICE',
  total: 0,
  subtotal: 0,
  tax: 0,
  status: 'draft',
  created: Math.floor(Date.now() / 1000),
  lines: {
    data: []
  },
  _isFakeEmptyInvoice: true
});

export default {
  name: "CustomerInvoiceComparison",
  components: {
    CustomerAutocomplete,
    CustomerInvoice,
    CustomerHeader,
    CouponApplyDialog,
    PlanPrices
  },
  mixins: [urlState],
  data() {
    return {
      showComparisons: [],
      selectedCustomer: null,
      customerUid: null,
      weekDate: moment().startOf('week').format('YYYY-MM-DD'),
      stripeDates: [],
      showDatePicker: false,
      showStripeDatePicker: false,
      loading: false,
      loadingCustomer: false,
      stripeInvoices: [],
      stripeCreditNotes: [],
      fedInvoices: [],
      invoiceComparisons: [],
      expandedFedInvoices: {},
      expandedFedRows: {},
      showStripeLineItems: true,
      showFedLineItems: true,
      showFedPriceDetails: false,
      lastFetchKey: null,
      applyingAdjustments: false,
      showIndividualInvoices: false,
      showVoidDialog: false,
      voidingInvoices: false,
      clearingOrder: false,
      showClearOrderDialog: false,
      orderItemsToDelete: [],
      voidingAndClearing: false,
      creditingAndCanceling: false,
      revokingCoupon: false,
      showRevokeCouponDialog: false,
      showAddCouponDialog: false,
    }
  },
  mounted() {
    // ensure plan pricing is available for the price-detail breakdown,
    // and streams (with their `visible` flag) for the cheaper-plan check
    this.$store.dispatch('fetchPlans');
    this.$store.dispatch('fetchStreams');
    this.syncToUrl({
      param: 'customerUid',
      urlParam: 'uid',
      initFromRoute: true
    });
    this.syncToUrl({
      param: 'weekDate',
      urlParam: 'weekDate',
      initFromRoute: true
    });
    this.syncToUrl({
      param: 'stripeDates',
      urlParam: 'stripeDates',
      initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : (v ? v.split(',') : []),
      transformCallback: (v) => Array.isArray(v) ? v.join(',') : v
    });

    this.$nextTick(() => {
      // Set default Stripe dates if not already set
      if (!this.stripeDates || this.stripeDates.length === 0) {
        this.setDefaultStripeDates();
      }
    })


    // Load customer if UID is in URL
    if (this.customerUid) {
      return this.loadCustomerFromUid();
    }
  },
  watch: {
    selectedCustomer: 'onCustomerChanged',
    customerUid: 'onCustomerUidChanged',
    weekDate: 'onWeekDateChanged',
    stripeDates: 'fetchInvoices'
  },
  methods: {
    formatCurrency,
    formatDate(timestamp) {
      return moment.unix(timestamp).format('MMM D, YYYY h:mm A');
    },
    redirectToMainAccount() {
      if (this.selectedCustomer && this.selectedCustomer.mainAccount) {
        this.$router.push({
          name: this.$route.name,
          params: this.$route.params,
          query: {
            ...this.$route.query,
            uid: this.selectedCustomer.mainAccount
          }
        });
      }
    },
    formatKey(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    // Fed moved Stripe accounts in October 2026 and the dashboard opens
    // whichever account the link names; a bare link opens the viewer's default
    // account, which for most people is still the old one. A live object id
    // carries the last ten characters of its account id, so an invoice or
    // credit note links to the account it lives on. Customer ids are the same
    // on both accounts (the copy kept them), so a customer links to the new
    // account, where the live customer is.
    stripeDashboardUrl(objectId, kind) {
      const isTest = process.env.VUE_APP_SENTRY_ENVIRONMENT === 'development';
      if (isTest) return `https://dashboard.stripe.com/test/${kind}/${objectId}`;
      const onOldAccount = typeof objectId === 'string' && objectId.includes(OLD_STRIPE_ACCOUNT.slice(-10));
      return `https://dashboard.stripe.com/${onOldAccount ? OLD_STRIPE_ACCOUNT : NEW_STRIPE_ACCOUNT}/${kind}/${objectId}`;
    },
    getStripeInvoiceUrl(invoiceId) {
      return this.stripeDashboardUrl(invoiceId, 'invoices');
    },
    getStripeCreditNoteUrl(creditNoteId) {
      return this.stripeDashboardUrl(creditNoteId, 'credit_notes');
    },
    getStripeCustomerUrl(stripeCustomerId) {
      return this.stripeDashboardUrl(stripeCustomerId, 'customers');
    },
    getOrderEditRoute() {
      const weekStart = moment(this.weekDate).startOf('week');
      const weekEnd = moment(this.weekDate).endOf('week');

      return {
        name: 'OrderEdit',
        query: {
          dates: [
            weekStart.format('YYYY-MM-DD'),
            weekEnd.format('YYYY-MM-DD')
          ],
          customer: this.selectedCustomer.uid
        }
      };
    },
    getStripeLineTax(line) {
      if (line.tax_amounts && line.tax_amounts.length) {
        return line.tax_amounts.reduce((sum, tax) => sum + tax.amount, 0);
      }
      return 0;
    },
    getFedItemTax(item) {
      return item.total_tax || 0;
    },
    priceExplanationFor(item) {
      try {
        const {summary, warnings} = explainLineItemPrice({
          lineItem: item,
          plans: this.$store.state.plans || {},
          mealCount: this.fedMealCount,
          user: this.selectedCustomer,
          verifyLineItemAmount: true,
        });
        return {summary, warnings: warnings || []};
      } catch (e) {
        return {summary: 'Unable to explain price: ' + e.message, warnings: []};
      }
    },
    // Per-row price-detail expansion (the checkbox is a master "show all" toggle)
    toggleFedRow(key) {
      this.$set(this.expandedFedRows, key, !this.expandedFedRows[key]);
    },
    isFedRowExpanded(key) {
      return this.showFedPriceDetails || !!this.expandedFedRows[key];
    },
    explainItemPrice(item) {
      return this.priceExplanationFor(item).summary;
    },
    explainItemWarnings(item) {
      return this.priceExplanationFor(item).warnings.join('; ');
    },
    getStatusColor(status) {
      const colors = {
        'paid': 'success',
        'open': 'warning',
        'draft': 'info',
        'void': 'error',
        'uncollectible': 'error'
      };
      return colors[status] || 'grey';
    },
    isSunday(date) {
      return moment(date).day() === 0;
    },
    closeDatePicker() {
      this.showDatePicker = false;
      this.fetchInvoices();
    },
    closeStripeDatePicker() {
      this.showStripeDatePicker = false;
    },
    openStripeDatePicker(type) {
      this.stripeDatePickerType = type;
      this.showStripeDatePicker = true;
    },
    setDefaultStripeDates() {
      // Always update Stripe dates when Fed week changes
      // Wednesday before the week date to the week date
      const thursdayBefore = moment(this.weekDate).startOf('week').add(4, 'days').subtract(1, 'week');
      const wednesdayAfter = moment(this.weekDate).startOf('week').add(3, 'days');

      if (moment(this.weekDate).isSameOrAfter('2025-12-21', 'date')) {
        thursdayBefore.subtract(1, 'day');
        wednesdayAfter.subtract(1, 'day');
      }

      this.stripeDates = [
        thursdayBefore.format('YYYY-MM-DD'),
        wednesdayAfter.format('YYYY-MM-DD')
      ];
    },
    onWeekDateChanged() {
      this.setDefaultStripeDates();
      this.fetchInvoices();
    },
    async loadCustomerFromUid() {
      if (!this.customerUid || this.loadingCustomer) return;

      // Don't reload if we already have this customer
      if (this.selectedCustomer && this.selectedCustomer.uid === this.customerUid) {
        return;
      }

      try {
        this.loadingCustomer = true;
        const response = await api.get(`v2/customer/${this.customerUid}`);
        this.selectedCustomer = response.data;
        console.log('Loaded customer:', this.selectedCustomer);
      } catch (error) {
        console.error('Failed to load customer:', error);
        this.$store.dispatch('showError', {
          message: 'Failed to load customer: ' + error.message
        });
      } finally {
        this.loadingCustomer = false;
      }
    },
    onCustomerChanged() {
      if (this.selectedCustomer && this.selectedCustomer.uid !== this.customerUid) {
        this.customerUid = this.selectedCustomer.uid;
      }
      // Only fetch if we have a valid customer with stripe data
      if (this.selectedCustomer && this.selectedCustomer.uid) {
        this.fetchInvoices();
      }
    },
    async onCustomerUidChanged() {
      if (this.customerUid && (!this.selectedCustomer || this.selectedCustomer.uid !== this.customerUid)) {
        await this.loadCustomerFromUid();
        // Fetch invoices after customer is loaded
        if (this.selectedCustomer) {
          this.fetchInvoices();
        }
      } else if (!this.customerUid) {
        this.selectedCustomer = null;
        this.stripeInvoices = [];
        this.stripeCreditNotes = [];
        this.fedInvoices = [];
        this.invoiceComparisons = [];
        this.lastFetchKey = null;
      }
    },
    async fetchInvoices(forceRefresh = false) {

      if (!this.selectedCustomer || !this.weekDate || !this.stripeDates || this.stripeDates.length < 2) {
        this.stripeInvoices = [];
        this.stripeCreditNotes = [];
        this.fedInvoices = [];
        this.invoiceComparisons = [];
        this.lastFetchKey = null;
        return;
      }

      // Create a unique key for this fetch to prevent duplicate requests
      const fetchKey = `${this.selectedCustomer.uid}-${this.weekDate}-${this.stripeDates.join(',')}`;

      // Skip if we're already fetching this exact combination (unless forced refresh)
      if (this.loading || (!forceRefresh && this.lastFetchKey === fetchKey)) {
        console.log('already loading or same fetch', this.loading, fetchKey, 'forceRefresh:', forceRefresh);
        return;
      }

      // console.log('fetchKey', fetchKey);
      this.loading = true;
      this.lastFetchKey = fetchKey;

      try {
        // Fetch Stripe invoices, credit notes, and Fed invoices in parallel
        const [stripeResponse, fedResponse] = await Promise.allSettled([
          this.fetchStripeInvoices(),
          this.fetchFedInvoices()
        ]);

        this.stripeInvoices = stripeResponse.status === 'fulfilled'
          ? stripeResponse.value.sort((a, b) => (a.created || 0) - (b.created || 0))
          : [];
        this.fedInvoices = fedResponse.status === 'fulfilled' ? fedResponse.value : [];


        // fetch credit notes for any stripe invoices
        this.stripeCreditNotes = this.stripeInvoices.length > 0
          ? (await this.fetchStripeCreditNotes(this.stripeInvoices))
            .sort((a, b) => (a.created || 0) - (b.created || 0))
          : [];


        // If no Stripe invoices but Fed invoices exist, create an empty fake Stripe invoice for comparison
        if (this.stripeInvoices.length === 0 && this.fedInvoices.length > 0) {
          this.stripeInvoices = [createFakeEmptyInvoice()];
        }

        // Compare invoices
        await this.compareInvoices();

      } catch (error) {
        console.error('Error fetching invoices:', error);
        this.$store.dispatch('showError', {
          message: 'Failed to fetch invoices: ' + error.message
        });
        this.lastFetchKey = null; // Reset on error so we can retry
      } finally {
        this.loading = false;
      }
    },
    async fetchStripeInvoices() {
      if (!this.selectedCustomer || !this.selectedCustomer.stripe_id) {
        console.warn('No stripe_id found for customer', this.selectedCustomer && this.selectedCustomer.stripe_id);
        return [];
      }

      try {
        // Start at noon Vancouver time on the from date
        // const from = moment.tz(fromDate, 'America/Vancouver').hour(12).minute(0).second(0);
        // const to = moment.tz(toDate, 'America/Vancouver').endOf('day');

        let params
        // if (moment(this.weekDate).isSameOrAfter('2025-12-14', 'date')){
        //   // after this date stripe has metadata of the order week
        //   params = {
        //     customer_id: this.selectedCustomer.stripe_id,
        //     weekDate: this.weekDate
        //   };
        // } else
        {
          params = {
            customer_id: this.selectedCustomer.stripe_id,
            weekDate: this.weekDate
          };
        }
        const response = await api.get('v2/stripe/invoices', {params});
        return response.data || [];
      } catch (error) {
        console.warn('Failed to fetch Stripe invoices:', error);
        return [];
      }
    },
    async fetchFedInvoices() {
      try {
        const response = await api.get(`v2/invoice/${this.selectedCustomer.uid}/${this.weekDate}`);

        if (response) {
          return [response.data];
        }

        return [];
      } catch (error) {
        console.warn('Failed to fetch Fed invoice:', error);
        return [];
      }
    },
    async fetchStripeCreditNotes(invoices) {
      if (!invoices || invoices.length === 0) {
        console.warn('no invoices to fetch');
        return [];
      }
      try {
        const invoiceIds = invoices.map(i => i.id);
        console.log('fetching credit notes for invoices:', invoiceIds);
        const response = await api.get('v2/stripe/credit-notes', {
          params: {
            invoiceIds
          }
        });
        console.log('loaded credit notes');
        return response.data || [];
      } catch (error) {
        console.warn('Failed to fetch Stripe credit notes:', error);
        return [];
      }
    },
    async compareInvoices() {
      this.invoiceComparisons = [];

      if (!this.combinedStripeInvoice || !this.fedInvoices.length) {
        return;
      }

      try {
        // Compare the combined Stripe invoice with the Fed invoice
        const matchingFedInvoice = this.fedInvoices[0]; // Compare with the first Fed invoice

        if (matchingFedInvoice) {
          const response = await api.post('v2/invoice/compare', {
            stripeInvoice: this.combinedStripeInvoice,
            fedInvoice: matchingFedInvoice
          });

          console.log('Invoice comparison response:', response.data);
          this.invoiceComparisons.push({
            ...response.data,
            stripeInvoice: this.combinedStripeInvoice,
            fedInvoice: matchingFedInvoice
          });
        }
      } catch (error) {
        console.error('Error comparing invoices:', error);
        alert('failed to compare ' + error.messa)
      }
    },
    async applyAdjustments() {
      if (!this.comparison || !this.comparison.stripeInvoice) {
        console.warn('No comparison or Stripe invoice available');
        return;
      }

      // Check if there are non-paid invoices
      if (this.nonPaidInvoices.length > 0) {
        this.showVoidDialog = true;
        return;
      }

      this.applyingAdjustments = true;

      try {
        const response = await api.post('v2/stripe/check-and-apply-adjustments', {
          weekDate: this.weekDate,
          billingUserId: this.customerUid,
          customerEmail: this.selectedCustomer.email,
        });

        if (response.data.success && response.data.action === 'no_changes') {
          // fed-lib found nothing to change: Stripe already matches the Fed
          // invoice once superseded $0 coupon invoices are ignored.
          this.$store.commit('showSnackbar', {
            message: `Stripe already matches the Fed invoice, nothing applied`,
            color: 'info'
          });
        } else if (response.data.success) {
          this.$store.commit('showSnackbar', {
            message: `Adjusted billing successfully`,
            color: 'success'
          });

          // Stripe's invoice-search index lags by ~15s after a write, so a
          // local refresh would show stale data. Prompt the user to reload
          // when they're ready — they can dismiss to keep working and refresh
          // manually later.
          if (window.confirm('Adjustments applied. Reload the page in a few seconds to see the changes?')) {
            window.location.reload();
          }
        } else {
          throw new Error('Failed to apply adjustments');
        }
      } catch (error) {
        console.error('Error applying adjustments:', error);
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to apply adjustments';
        this.$store.commit('showSnackbar', {
          message: `Error: ${message}`,
          color: 'error'
        });
      } finally {
        this.applyingAdjustments = false;
      }
    },
    cancelVoidDialog() {
      this.showVoidDialog = false;
    },
    async voidInvoicesAndProceed() {
      this.voidingInvoices = true;

      try {
        // Void all non-paid invoices
        for (const invoice of this.nonPaidInvoices) {
          await api.post('v2/stripe/void-invoice', {
            invoice_id: invoice.id
          });
        }

        this.$store.commit('showSnackbar', {
          message: `${this.nonPaidInvoices.length} invoice(s) voided successfully. You can now try applying adjustments again.`,
          color: 'success'
        });

        this.showVoidDialog = false;

        // Refresh invoices to show updated status
        await this.fetchInvoices(true);

      } catch (error) {
        console.error('Error voiding invoices:', error);
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to void invoices';
        this.$store.commit('showSnackbar', {
          message: `Error: ${message}`,
          color: 'error'
        });
      } finally {
        this.voidingInvoices = false;
      }
    },
    // Void only: the Fed order stays, so the week shows as owing and can be billed again
    async voidFailedInvoices() {
      const invoices = this.failedPaymentInvoices;
      const confirmed = confirm(`This will void ${invoices.length} failed payment invoice(s) for ${this.selectedCustomer.first_name} ${this.selectedCustomer.last_name} for week ${this.weekFormatted}. The Fed order is kept. A voided invoice cannot be reopened. Continue?`);
      if (!confirmed) return;

      this.voidingInvoices = true;
      const failures = [];
      for (const invoice of invoices) {
        try {
          await api.post('v2/stripe/void-invoice', {invoice_id: invoice.id});
        } catch (error) {
          console.error(`Failed to void invoice ${invoice.id}:`, error);
          const data = (error.response && error.response.data) || {};
          failures.push(`${invoice.id}: ${data.error || data.message || error.message}`);
        }
      }
      this.$store.commit('showSnackbar', failures.length
        ? {message: `Error: ${failures.length} of ${invoices.length} invoice(s) not voided. ${failures.join('; ')}`, color: 'error'}
        : {message: `${invoices.length} invoice(s) voided. The Fed order is kept; apply adjustments to bill the week again.`, color: 'success'});
      this.voidingInvoices = false;
      await this.fetchInvoices(true);
    },
    async clearOrder() {
      if (!this.selectedCustomer || !this.weekDate) {
        console.warn('No customer or week date selected');
        return;
      }

      // Fetch order items to show in dialog
      this.orderItemsToDelete = this.fedInvoices[0].invoiceItems;
      this.showClearOrderDialog = true;
    },
    cancelClearOrder() {
      this.showClearOrderDialog = false;
      this.orderItemsToDelete = [];
    },
    async confirmClearOrder() {
      this.clearingOrder = true;

      try {
        const response = await api.delete(`v2/order/clear/${this.selectedCustomer.uid}/${this.weekDate}`, {params: {include_subaccounts: true}});

        // Check if the response indicates success
        if (response.data && response.data.success) {
          this.$store.commit('showSnackbar', {
            message: response.data.message || `Order cleared successfully for ${this.selectedCustomer.first_name} ${this.selectedCustomer.last_name}`,
            color: 'success'
          });

          // Close dialog and reset state
          this.showClearOrderDialog = false;
          this.orderItemsToDelete = [];

          // Refresh the Fed invoices to reflect the cleared order
          await this.fetchInvoices(true);
        } else {
          // Response doesn't indicate success
          const errorMessage = response.data?.message || 'Unexpected response from server';
          throw new Error(errorMessage);
        }

      } catch (error) {
        console.error('Error clearing order:', error);
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to clear order';
        this.$store.commit('showSnackbar', {
          message: `Error: ${message}`,
          color: 'error'
        });
      } finally {
        this.clearingOrder = false;
      }
    },
    async voidInvoiceAndClearOrders() {
      if (!this.failedPaymentInvoices || this.failedPaymentInvoices.length === 0) {
        this.$store.commit('showSnackbar', {
          message: 'No failed payment invoices to void',
          color: 'warning'
        });
        return;
      }

      const confirmed = confirm(`This will void ${this.failedPaymentInvoices.length} failed payment invoice(s) and clear all Fed orders for ${this.selectedCustomer.first_name} ${this.selectedCustomer.last_name} for week ${this.weekFormatted}. This action cannot be undone. Continue?`);

      if (!confirmed) {
        return;
      }

      this.voidingAndClearing = true;

      try {
        // Step 1: Void all failed payment invoices
        let voidedCount = 0;
        for (const invoice of this.failedPaymentInvoices) {
          try {
            await api.post('v2/stripe/void-invoice', {
              invoice_id: invoice.id
            });
            voidedCount++;
          } catch (error) {
            console.error(`Failed to void invoice ${invoice.id}:`, error);
            // Continue with other invoices even if one fails
          }
        }

        // Step 2: Clear Fed orders
        const clearResponse = await api.delete(`v2/order/clear/${this.selectedCustomer.uid}/${this.weekDate}`, {
          params: {include_subaccounts: true}
        });

        if (!clearResponse.data || !clearResponse.data.success) {
          throw new Error(clearResponse.data?.message || 'Failed to clear orders');
        }

        // Success message
        this.$store.commit('showSnackbar', {
          message: `Successfully voided ${voidedCount} invoice(s) and cleared Fed orders for ${this.selectedCustomer.first_name} ${this.selectedCustomer.last_name}`,
          color: 'success'
        });

        // Refresh data to show changes
        await this.fetchInvoices(true);

      } catch (error) {
        console.error('Error voiding invoices and clearing orders:', error);
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to void invoices and clear orders';
        this.$store.commit('showSnackbar', {
          message: `Error: ${message}`,
          color: 'error'
        });
      } finally {
        this.voidingAndClearing = false;
      }
    },
    async creditAndCancelOrder() {
      if (!this.paidInvoices || this.paidInvoices.length === 0) {
        this.$store.commit('showSnackbar', {
          message: 'No paid invoices found to credit',
          color: 'warning'
        });
        return;
      }

      const confirmed = confirm(`This will credit ${this.formatCurrency(this.netPaidAmount)} and cancel all Fed orders for ${this.selectedCustomer.first_name} ${this.selectedCustomer.last_name} for week ${this.weekFormatted}. This action cannot be undone. Continue?`);

      if (!confirmed) {
        return;
      }

      this.creditingAndCanceling = true;

      try {
        // Step 1: Clear Fed orders first
        this.$store.commit('showSnackbar', {
          message: 'Step 1/3: Clearing Fed orders...',
          color: 'info'
        });

        const clearResponse = await api.delete(`v2/order/clear/${this.selectedCustomer.uid}/${this.weekDate}`, {
          params: {include_subaccounts: true}
        });

        if (!clearResponse.data || !clearResponse.data.success) {
          throw new Error(clearResponse.data?.message || 'Failed to clear orders');
        }

        // Step 2: Refresh invoices and perform new comparison
        this.$store.commit('showSnackbar', {
          message: 'Step 2/3: Refreshing invoices and comparing...',
          color: 'info'
        });

        await this.fetchInvoices(true);

        // Wait a moment for comparison to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (!this.comparison) {
          throw new Error('Failed to generate comparison after clearing orders');
        }

        // Step 3: Apply adjustments to create credit notes for all items
        this.$store.commit('showSnackbar', {
          message: 'Step 3/3: Creating credit notes...',
          color: 'info'
        });

        const creditResponse = await api.post('v2/stripe/check-and-apply-adjustments', {
          weekDate: this.weekDate,
          billingUserId: this.customerUid,
          customerEmail: this.selectedCustomer.email,
        });

        if (!creditResponse.data || !creditResponse.data.success) {
          throw new Error(creditResponse.data?.message || 'Failed to create credit notes');
        }

        const creditedTotal = (creditResponse.data.adjustments || [])
          .filter(a => a.type === 'credit_note')
          .reduce((sum, a) => sum + (a.amount || 0), 0);

        // Success message
        this.$store.commit('showSnackbar', {
          message: `Successfully credited ${this.paidInvoices.length} invoice(s) and cancelled Fed orders for ${this.selectedCustomer.first_name} ${this.selectedCustomer.last_name}. Credit total: $${(creditedTotal / 100).toFixed(2)}`,
          color: 'success'
        });

        // Final refresh to show the credited state
        await this.fetchInvoices(true);

      } catch (error) {
        console.error('Error crediting and canceling order:', error);
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to credit and cancel order';
        this.$store.commit('showSnackbar', {
          message: `Error: ${message}`,
          color: 'error'
        });
      } finally {
        this.creditingAndCanceling = false;
      }
    },
    onCouponApplied() {
      // the week's Stripe invoices changed (credit notes, a new invoice); as
      // after applying adjustments, Stripe's search index lags a reload by
      // some seconds, so the person chooses when to reload
      if (window.confirm('Coupon applied and the invoice reissued. Reload the page in a few seconds to see the changes?')) {
        window.location.reload();
      }
    },
    openRevokeCouponDialog() {
      if (!this.selectedCustomer || !this.weekDate) {
        console.warn('No customer or week date selected');
        return;
      }
      this.showRevokeCouponDialog = true;
    },
    cancelRevokeCoupon() {
      this.showRevokeCouponDialog = false;
    },
    async confirmRevokeCoupon() {
      if (!this.selectedCustomer || !this.weekDate) {
        return;
      }

      this.revokingCoupon = true;

      try {
        const response = await api.post('v2/stripe/revoke-coupon', {
          uid: this.customerUid,
          weekDate: this.weekDate,
        });

        if (!response.data || !response.data.success) {
          throw new Error(response.data?.message || 'Failed to revoke coupon');
        }

        const revoked = response.data.revoked || [];
        this.$store.commit('showSnackbar', {
          message: revoked.length
            ? `Revoked coupon${revoked.length > 1 ? 's' : ''} ${revoked.join(', ')}. Fed invoice recalculated without the discount.`
            : 'Coupon revoked. Fed invoice recalculated without the discount.',
          color: 'success'
        });

        this.showRevokeCouponDialog = false;

        // Refresh so the recalculated Fed invoice (and any Stripe adjustments
        // required) are reflected on the page.
        await this.fetchInvoices(true);

      } catch (error) {
        console.error('Error revoking coupon:', error);
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Failed to revoke coupon';
        this.$store.commit('showSnackbar', {
          message: `Error: ${message}`,
          color: 'error'
        });
      } finally {
        this.revokingCoupon = false;
      }
    }
  },
  computed: {
    // Discount applied for this week, taken from the Fed invoice (source of
    // truth for what the customer was charged) and falling back to the Stripe
    // invoice's discount if the Fed invoice doesn't carry one.
    couponDiscountAmount() {
      const fedDiscount = (this.fedInvoices[0] && this.fedInvoices[0].totalDiscount) || 0;
      if (fedDiscount > 0) return fedDiscount;
      const stripeDiscounts = this.combinedStripeInvoice && this.combinedStripeInvoice.total_discount_amounts;
      return Array.isArray(stripeDiscounts)
        ? stripeDiscounts.reduce((sum, d) => sum + (d.amount || 0), 0)
        : 0;
    },
    couponDiscountUsed() {
      return this.couponDiscountAmount > 0;
    },
    realStripeInvoices() {
      return this.stripeInvoices.filter(i => !i._isFakeEmptyInvoice)
    },
    adjustments() {
      let items = (this.comparison && this.comparison.items) || {};
      return {
        itemsToAdd: (items.missing_in_stripe || []), //.filter(i => i.amount !== 0),
        itemsToRemove: (items.missing_in_fed || []).filter(i => i.amount !== 0)
      };
    },
    countAdjustments() {
      return this.adjustments.itemsToAdd.length + this.adjustments.itemsToRemove.length;
    },
    hasAdjustments() {
      return this.countAdjustments > 0
    },
    comparison() {
      return this.invoiceComparisons[0];
    },
    weekFormatted() {
      if (!this.weekDate) return '';
      const weekStart = moment(this.weekDate).startOf('week');
      const weekEnd = moment(this.weekDate).endOf('week');
      return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
    },
    stripeDatesFormatted() {
      if (!this.stripeDates || this.stripeDates.length === 0) return '';
      if (this.stripeDates.length === 1) {
        return moment(this.stripeDates[0]).format('MMM D, YYYY');
      }
      const [fromDate, toDate] = [...this.stripeDates].sort();
      return `${moment(fromDate).format('MMM D')} - ${moment(toDate).format('MMM D, YYYY')}`;
    },
    stripeStartDayName() {
      if (!this.stripeDates || this.stripeDates.length === 0) return '';
      const [fromDate] = [...this.stripeDates].sort();
      return moment(fromDate).format('dddd');
    },
    stripeEndDayName() {
      if (!this.stripeDates || this.stripeDates.length === 0) return '';
      const [fromDate, toDate] = [...this.stripeDates].sort();
      return moment(toDate || fromDate).format('dddd');
    },
    // The week's invoices that still count, newest first. A $0 coupon-covered
    // invoice cannot take a credit note, so a reissue leaves the old one in
    // Stripe; the server (fed-lib combineStripeInvoices) ignores it, and so
    // must this page, or it keeps listing the old lines as removals that the
    // apply call will never make.
    // A fully refunded invoice (refunded outright, not through a credit note)
    // is money the customer no longer paid, so it does not count either; it
    // is listed and linked, not combined.
    currentStripeInvoices() {
      const {invoices, dropped} = dropStaleCouponZeroInvoices(this.stripeInvoices || []);
      const refunded = invoices.filter(isFullyRefunded);
      let kept = invoices.filter(invoice => !isFullyRefunded(invoice));
      if (kept.length === 0 && refunded.length > 0) kept = [createFakeEmptyInvoice()];
      return {invoices: kept, dropped, refunded};
    },
    staleStripeInvoices() {
      return this.currentStripeInvoices.dropped;
    },
    refundedStripeInvoices() {
      return this.currentStripeInvoices.refunded;
    },
    combinedStripeInvoice() {
      const stripeInvoices = this.currentStripeInvoices.invoices;
      if (!stripeInvoices.length) return null;

      // Combine multiple invoices into one
      const allLines = [];
      let totalAmount = 0;
      let totalSubtotal = 0;
      let totalTax = 0;
      let totalDiscount = 0;
      let totalRefunded = 0;
      const invoiceNumbers = [];

      // Collect all credited line items from credit notes
      const creditedLineItems = new Set();
      this.stripeCreditNotes.forEach(creditNote => {
        if (creditNote.lines && creditNote.lines.data) {
          creditNote.lines.data.forEach(line => {
            if (line.invoice_line_item) {
              creditedLineItems.add(line.invoice_line_item);
            }
          });
        }
      });

      stripeInvoices.forEach(invoice => {
        // Collect line items that haven't been credited
        if (invoice.lines && invoice.lines.data) {
          const uncreditedLines = invoice.lines.data.filter(line => !creditedLineItems.has(line.id));
          allLines.push(...uncreditedLines);
        }

        // Calculate totals excluding credited amounts
        let invoiceTotal = invoice.total || 0;
        let invoiceSubtotal = invoice.subtotal || 0;
        let invoiceTax = invoice.tax || 0;

        // Subtract credit note amounts from this invoice
        this.stripeCreditNotes.forEach(creditNote => {
          if (creditNote.invoice === invoice.id) {
            invoiceTotal -= creditNote.amount || 0;
            invoiceSubtotal -= creditNote.subtotal || 0;
            let creditTaxAmount = creditNote.total_taxes;
            // console.log('creditNote.total_tax_amounts', creditTaxAmount, creditNote)
            if (creditTaxAmount) {
              invoiceTax -= creditNote.total_taxes.reduce((sum, tax) => sum + tax.amount, 0);
            }
          }
        });

        totalAmount += invoiceTotal;
        totalSubtotal += invoiceSubtotal;
        totalTax += invoiceTax;
        totalDiscount += invoice.total_discount_amounts ?
          invoice.total_discount_amounts.reduce((sum, discount) => sum + discount.amount, 0) : 0;
        // a partial refund outside a credit note: the customer paid that much less
        totalRefunded += refundedAmount(invoice);

        // Collect invoice numbers/IDs for reference
        invoiceNumbers.push(invoice.number || invoice.id);
      });

      // Create combined invoice object based on the newest invoice's structure
      const firstInvoice = stripeInvoices[0];
      return {
        ...firstInvoice,
        // id: `combined-${invoiceNumbers.join('-')}`, //use first invoice
        number: `Combined (${invoiceNumbers.join(', ')})`,
        total: totalAmount - totalRefunded,
        subtotal: totalSubtotal,
        tax: totalTax,
        total_discount_amounts: totalDiscount > 0 ? [{amount: totalDiscount}] : [],
        amount_refunded: totalRefunded,
        lines: {
          data: allLines,
          total_count: allLines.length
        },
        _isCombined: true,
        _originalInvoices: stripeInvoices
      };
    },
    sortedStripeInvoices() {
      if (!this.stripeInvoices || !this.stripeInvoices.length) return [];

      // If showing individual invoices or only one invoice, return individual invoices
      if (this.showIndividualInvoices || this.stripeInvoices.length === 1) {
        return this.stripeInvoices.map(invoice => {
          const allLines = invoice.lines && invoice.lines.data ? invoice.lines.data : [];

          // Separate shipping items from regular items
          const regularLines = [];
          const shippingLines = [];

          allLines.forEach(line => {
            const description = (line.description || '').toLowerCase();
            if (description.includes('shipping') || description.includes('delivery to')) {
              shippingLines.push(line);
            } else {
              regularLines.push(line);
            }
          });

          // Sort regular items alphabetically by description
          const sortedRegular = regularLines.sort((a, b) => {
            const descA = a.description || '';
            const descB = b.description || '';
            return descA.localeCompare(descB);
          });

          // Sort shipping items alphabetically by description
          const sortedShipping = shippingLines.sort((a, b) => {
            const descA = a.description || '';
            const descB = b.description || '';
            return descA.localeCompare(descB);
          });

          return {
            ...invoice,
            regularLines: sortedRegular,
            shippingLines: sortedShipping
          };
        });
      }

      // Otherwise, return the combined invoice
      if (!this.combinedStripeInvoice) return [];

      const invoice = this.combinedStripeInvoice;
      const allLines = invoice.lines && invoice.lines.data ? invoice.lines.data : [];

      // Separate shipping items from regular items
      const regularLines = [];
      const shippingLines = [];

      allLines.forEach(line => {
        const description = (line.description || '').toLowerCase();
        if (description.includes('shipping') || description.includes('delivery to')) {
          shippingLines.push(line);
        } else {
          regularLines.push(line);
        }
      });

      // Sort regular items alphabetically by description
      const sortedRegular = regularLines.sort((a, b) => {
        const descA = a.description || '';
        const descB = b.description || '';
        return descA.localeCompare(descB);
      });

      // Sort shipping items alphabetically by description
      const sortedShipping = shippingLines.sort((a, b) => {
        const descA = a.description || '';
        const descB = b.description || '';
        return descA.localeCompare(descB);
      });

      return [{
        ...invoice,
        regularLines: sortedRegular,
        shippingLines: sortedShipping
      }];
    },
    // Approximate the week's meal count (excluding extras/snacks) that drives
    // the plan's volume pricing range. Mirrors fed-lib countMeals for a single
    // customer's fed invoice.
    fedMealCount() {
      let count = 0;
      (this.sortedFedInvoices || []).forEach(inv => {
        (inv.regularItems || []).forEach(item => {
          if (item.tod !== 'extras' && item.tod !== 'snack') {
            count += Number(item.quantity) || 0;
          }
        });
      });
      return count;
    },
    // Quick check: would the fed line items be cheaper on any plan available on
    // the website? Candidate plans are those whose stream is visible (i.e. sold
    // on app.fedfedfed.com), plus 'personal'. Each candidate re-prices the items
    // via comparePlanCost (which re-derives substitution status per plan stream).
    planCostCheck() {
      const plans = this.$store.state.plans || {};
      const streams = this.$store.state.streams || {};
      if (!Object.keys(plans).length) return null;

      const lineItems = [];
      (this.sortedFedInvoices || []).forEach(inv => {
        (inv.regularItems || []).forEach(item => lineItems.push(item));
      });
      if (!lineItems.length) return null;

      const isCandidate = id => {
        const p = plans[id];
        // only plans with real range pricing (avoid the hard-coded fallback)
        if (!p || !(Array.isArray(p.ranges) && p.ranges.length)) return false;
        if (id === 'personal') return true;
        // website plans share their id with a visible stream
        return !!(streams[id] && streams[id].visible);
      };
      const candidateIds = Object.keys(plans).filter(isCandidate);
      if (!candidateIds.length) return null;

      const mealCount = this.fedMealCount;
      const user = this.selectedCustomer;

      let currentTotal = 0;
      const options = candidateIds.map(id => {
        const r = comparePlanCost({lineItems, plans, comparisonPlan: id, mealCount, user});
        currentTotal = r.currentTotal; // identical across candidates (items priced as-is)
        return {
          planId: id,
          planName: (plans[id] && plans[id].name) || id,
          total: r.comparisonTotal,
          difference: r.difference,
        };
      }).sort((a, b) => a.total - b.total);

      // distinct plan id(s) the items are currently on
      const currentPlanIds = [...new Set(lineItems.map(i => i.plan).filter(Boolean))];
      const currentPlanLabel = currentPlanIds.length ? currentPlanIds.join(', ') : 'current';

      const cheaper = options.filter(o => o.difference < 0);
      return {currentTotal, currentPlanLabel, options, cheaper, cheapest: options[0]};
    },
    sortedFedInvoices() {
      if (!this.fedInvoices || !this.fedInvoices.length) return [];

      return this.fedInvoices.map(invoice => {
        const allItems = invoice.invoiceItems || [];

        // Separate shipping items from regular items
        const regularItems = [];
        const shippingItems = [];

        allItems.forEach(item => {
          const description = (item.description || '').toLowerCase();
          if (description.includes('shipping') || description.includes('delivery to')) {
            shippingItems.push(item);
          } else {
            regularItems.push(item);
          }
        });

        // Sort regular items by ship_date first, then by description
        const sortedRegular = regularItems.sort((a, b) => {
          const dateA = a.ship_date || '';
          const dateB = b.ship_date || '';
          const dateCompare = dateA.localeCompare(dateB);

          if (dateCompare !== 0) {
            return dateCompare;
          }

          const descA = a.description || '';
          const descB = b.description || '';
          return descA.localeCompare(descB);
        });

        // Sort shipping items by ship_date first, then by description
        const sortedShipping = shippingItems.sort((a, b) => {
          const dateA = a.ship_date || '';
          const dateB = b.ship_date || '';
          const dateCompare = dateA.localeCompare(dateB);

          if (dateCompare !== 0) {
            return dateCompare;
          }

          const descA = a.description || '';
          const descB = b.description || '';
          return descA.localeCompare(descB);
        });

        return {
          ...invoice,
          regularItems: sortedRegular,
          shippingItems: sortedShipping
        };
      });
    },
    adjustmentSummary() {
      if (!this.comparison) return null;

      const removeAmount = this.comparison.items.missing_in_fed.reduce((sum, item) => {
        return sum + (item.amount || item.unit_amount_excluding_tax * item.quantity || 0);
      }, 0);

      const addAmount = this.comparison.items.missing_in_stripe.reduce((sum, item) => {
        return sum + (item.unit_amount * item.quantity || 0);
      }, 0);

      const quantityChangeAmount = this.comparison.items.quantity_differences.reduce((sum, item) => {
        // Assuming we need to calculate the monetary impact of quantity changes
        // This would need the unit price, but we don't have it directly in the differences
        return sum + (item.difference * 0); // Placeholder - would need more data
      }, 0);

      const amountChangeAmount = this.comparison.items.amount_differences.reduce((sum, item) => {
        return sum + item.difference;
      }, 0);

      const netChange = addAmount - removeAmount + quantityChangeAmount + amountChangeAmount;

      return {
        removeAmount,
        addAmount,
        quantityChangeAmount,
        amountChangeAmount,
        netChange
      };
    },
    nonPaidInvoices() {
      if (!this.stripeInvoices || this.stripeInvoices.length === 0) return [];
      return this.stripeInvoices.filter(invoice =>
        invoice.status && !(invoice.status === 'paid' || invoice.status === 'void') && !invoice._isFakeEmptyInvoice
      );
    },
    failedPaymentInvoices() {
      if (!this.stripeInvoices || this.stripeInvoices.length === 0) return [];
      return this.stripeInvoices.filter(invoice =>
        invoice.status === 'open' && invoice.attempt_count && invoice.attempt_count > 0 && !invoice._isFakeEmptyInvoice
      );
    },
    totalOrderValue() {
      return this.orderItemsToDelete.reduce((sum, item) => sum + (item.total || 0), 0);
    },
    paidInvoices() {
      if (!this.stripeInvoices || this.stripeInvoices.length === 0) return [];
      return this.stripeInvoices.filter(invoice =>
        invoice.status === 'paid' && !invoice._isFakeEmptyInvoice && !isFullyRefunded(invoice)
      );
    },
    totalInvoiceAmount() {
      const invoiceTotal = this.paidInvoices.reduce((sum, invoice) => {
        return sum + (invoice.total || 0);
      }, 0);
      return invoiceTotal - this.totalCreditNoteAmount;
    },
    actualAmountPaid() {
      const amountPaid = this.paidInvoices.reduce((sum, invoice) => {
        return sum + (invoice.amount_due || 0);
      }, 0);
      return amountPaid;
    },
    totalCreditUsed() {
      return this.paidInvoices.reduce((sum, invoice) => {
        const total = invoice.total || 0;
        const amountDue = invoice.amount_due || 0;
        return sum + (total - amountDue);
      }, 0);
    },
    totalPaidAmount() {
      // Keep this for backward compatibility - now same as actualAmountPaid
      return this.actualAmountPaid;
    },
    totalCreditNoteAmount() {
      if (!this.stripeCreditNotes || this.stripeCreditNotes.length === 0) return 0;
      return this.stripeCreditNotes.reduce((sum, creditNote) => {
        return sum + (creditNote.amount || 0);
      }, 0);
    },
    netPaidAmount() {
      return (this.totalPaidAmount);
    },
    creditRemaining() {
      return this.totalCreditUsed - this.totalCreditNoteAmount;
    },
    billingDate() {
      if (!this.weekDate) return null;
      // Billing date is Wed at noon, three days before the week date (Sunday)
      // Week starts on Sunday, so Wed is 3 days after start of week, minus 1 week
      return moment(this.weekDate).startOf('week').add(3, 'days').subtract(1, 'week').hour(12).minute(0).second(0);
    },
    billingDateFormatted() {
      if (!this.billingDate) return '';
      return this.billingDate.format('ddd, MMM D, YYYY [at] h:mm A');
    },
    isAfterBillingDate() {
      if (!this.billingDate) return false;
      return moment().isAfter(this.billingDate);
    },
    isBeforeBillingTime() {
      // Returns true if current time is before noon on billing date
      if (!this.billingDate) return false;
      return moment().isBefore(this.billingDate) && false;
    },
    isBillingInProgress() {
      // Returns true if:
      // 1. Current time is between noon (billing time) and 2 PM on billing date
      // 2. No Stripe invoice has been loaded (excluding fake invoices)
      if (!this.billingDate) return false;

      const now = moment();
      const billingEndTime = moment(this.billingDate).add(2, 'hours'); // 2 PM

      const isInTimeWindow = now.isAfter(this.billingDate) && now.isBefore(billingEndTime);
      const hasNoRealStripeInvoice = !this.stripeInvoices.some(inv => !inv._isFakeEmptyInvoice);

      return isInTimeWindow && hasNoRealStripeInvoice;
    },
    isOrderTooOld() {
      // Returns true if current time is more than one week after the billing date
      if (!this.billingDate) return false;
      const oneWeekAfterBilling = moment(this.billingDate).add(1, 'week');
      return moment().isAfter(oneWeekAfterBilling);
    }
  },
}
</script>

<style scoped>
::v-deep .v-expansion-panel-header {
  min-height: 48px;
}

::v-deep .v-expansion-panel-content__wrap {
  padding: 16px;
}
</style>